import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { formatDate } from '../lib/dates';
import { requestsApi, type StudentRequest } from '../lib/requestsApi';

const TYPES = ['All', 'Leave', 'DressCode', 'DropChange', 'Other'] as const;

/// Staff review inbox for student requests (leave / dress-code / drop-change / other).
/// The approval gate lives in the domain; this is the surface for acting on it. Staff-gated.
export function RequestsPage() {
  const [type, setType] = useState<typeof TYPES[number]>('All');
  const [rows, setRows] = useState<StudentRequest[] | null>(null);

  const load = useCallback(() => {
    requestsApi.pending(type === 'All' ? undefined : type).then(setRows).catch(() => setRows([]));
  }, [type]);
  useEffect(load, [load]);

  const act = async (id: string, decision: 'approve' | 'decline' | 'acknowledge') => {
    await requestsApi.review(id, decision);
    load();
  };

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Requests</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Request inbox</h1>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {TYPES.map((t) => (
          <button key={t} onClick={() => setType(t)} className="btn" style={{ padding: '4px 12px', fontSize: 13, background: type === t ? 'var(--accent,#C1272D)' : undefined, color: type === t ? '#fff' : undefined }}>
            {t === 'DressCode' ? 'Dress-code' : t === 'DropChange' ? 'Drop-change' : t}
          </button>
        ))}
      </div>

      {!rows ? <p style={{ color: 'var(--ink-3)' }}>Loading…</p> :
        rows.length === 0 ? <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>No pending requests.</div> :
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 680 }}>
            {rows.map((r) => <RequestCard key={r.id} r={r} onAct={act} />)}
          </div>}
    </div>
  );
}

function RequestCard({ r, onAct }: { r: StudentRequest; onAct: (id: string, d: 'approve' | 'decline' | 'acknowledge') => void }) {
  const typeLabel = r.type === 'DressCode' ? 'Dress-code' : r.type === 'DropChange' ? 'Drop-change' : r.type;
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
        <span className="pill pill-muted" style={{ fontSize: 11 }}>{typeLabel}</span>
        <strong>{r.title}</strong>
        <span style={{ fontSize: 12, color: 'var(--ink-3)', marginLeft: 'auto' }}>{formatDate(r.submittedAt)}</span>
      </div>

      <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 8 }}>
        {r.type === 'Leave' && r.fromDate && <Line label="Dates">{r.fromDate} → {r.toDate}</Line>}
        {r.type === 'DropChange' && <><Line label="For">{r.forDate}</Line><Line label="Drop at">{r.location}</Line></>}
        {r.detail && <Line label="Detail">{r.detail}</Line>}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button className="btn btn-accent" onClick={() => onAct(r.id, 'approve')}>Approve</button>
        <button className="btn" onClick={() => onAct(r.id, 'decline')}>Decline</button>
        <button className="btn" onClick={() => onAct(r.id, 'acknowledge')} style={{ marginLeft: 'auto' }}>Acknowledge</button>
      </div>
    </div>
  );
}

function Line({ label, children }: { label: string; children: ReactNode }) {
  return <div style={{ display: 'flex', gap: 8 }}><span style={{ minWidth: 64, color: 'var(--ink-3)', fontSize: 12 }}>{label}</span><span>{children}</span></div>;
}
